import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { NEVER, Observable, Subject, defer, take, takeUntil } from 'rxjs';
import { ConfirmationDialogComponent } from '../components/confirmation-dialog/confirmation-dialog.component';
import { FeedbackService } from './feedback.service';
import { PatientAppointmentService } from './patient-appointment.service';

/**
 * What the user chose when told the (now deceased) patient still has upcoming
 * appointments.
 *
 * - `review`    - "Review Appointments". The caller decides where to take the user.
 * - `cancelled` - "Cancel All", confirmed. The appointments have already been
 *                 deleted by this service (see `cancelledCount` / `error`).
 * - `kept`      - "Cancel All", then backed out of the confirmation. Nothing happened.
 * - `ignored`   - "Ignore".
 * - `dismissed` - dialog closed without picking anything (backdrop / escape).
 */
export type FutureAppointmentAction = 'review' | 'cancelled' | 'kept' | 'ignored' | 'dismissed';

export interface FutureAppointmentOutcome {
  action: FutureAppointmentAction;
  appointments: any[];
  /** Only set for `cancelled`. */
  cancelledCount?: number;
  /** Only set for `cancelled` when one or more deletions failed. */
  error?: any;
}

/**
 * Shared "this patient is deceased but still has appointments booked" prompt.
 *
 * Used by the M&M note path and the discharge-with-mortality path. The service
 * owns the dialogs and the bulk cancellation; it deliberately owns *no*
 * navigation - it reports the user's choice back to the caller, because the two
 * call sites sit in different places in their own flows and navigate differently.
 * `scrollToPatientAppointments()` is offered as an opt-in helper the caller may
 * choose to invoke, not something this service does on its own.
 */
@Injectable({
  providedIn: 'root'
})
export class DeceasedAppointmentCheckService {

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private feedbackService: FeedbackService,
    private appointmentService: PatientAppointmentService
  ) { }

  /**
   * Watches the already-loaded patient appointment stream and prompts as soon as
   * future appointments are seen. Use this where the patient file is open and
   * `patientAppointments$` is already populated for the patient.
   *
   * @param until$ tears down the appointment subscription - pass the component's
   *               `destroy$`. Note this does NOT cut off the dialog outcome: once
   *               the prompt is up the result is always delivered, which is what
   *               callers that navigate away immediately depend on.
   *
   * Emits at most once, then completes. Emits nothing at all when the patient has
   * no future appointments.
   */
  watchForFutureAppointments(patientId: string, until$: Observable<any> = NEVER): Observable<FutureAppointmentOutcome> {
    return defer(() => {
      const outcome$ = new Subject<FutureAppointmentOutcome>();

      if (!patientId) {
        outcome$.complete();
        return outcome$;
      }

      let prompted = false;
      this.appointmentService.patientAppointments$
        .pipe(takeUntil(until$))
        .subscribe({
          next: (appointments) => {
            if (!appointments || prompted) return;

            const futureAppointments = this.filterFutureAppointments(appointments);
            if (futureAppointments.length === 0) return;

            prompted = true;
            this.promptForFutureAppointments(futureAppointments, outcome$);
          },
          error: (error) => {
            console.error('Error checking future appointments:', error);
          },
          complete: () => {
            // Only safe to close the outcome off if no dialog is pending.
            if (!prompted) outcome$.complete();
          }
        });

      return outcome$;
    });
  }

  /**
   * One-shot variant: fetches the patient's appointments and prompts on the
   * result. Use this where the appointment stream may not be loaded for the
   * patient, or where the caller navigates away as part of the same action (a
   * stream subscription torn down by that navigation would silently no-op).
   *
   * Emits at most once, then completes.
   */
  checkForFutureAppointments(patientId: string): Observable<FutureAppointmentOutcome> {
    return defer(() => {
      const outcome$ = new Subject<FutureAppointmentOutcome>();

      if (!patientId) {
        outcome$.complete();
        return outcome$;
      }

      this.appointmentService.loadAppointmentsByPatient(patientId)
        .pipe(take(1))
        .subscribe({
          next: (response: any) => {
            const appointments = Array.isArray(response) ? response : (response?.data || []);
            const futureAppointments = this.filterFutureAppointments(appointments);

            if (futureAppointments.length === 0) {
              outcome$.complete();
              return;
            }

            this.promptForFutureAppointments(futureAppointments, outcome$);
          },
          error: (error) => {
            console.error('Error checking future appointments:', error);
            outcome$.complete();
          }
        });

      return outcome$;
    });
  }

  /**
   * Optional navigation helper for the `review` outcome - takes the user to the
   * patient file and asks it to scroll to the appointments section.
   */
  scrollToPatientAppointments(patientId: string) {
    this.router.navigate(['/patients', { outlets: { patient: ['file', patientId] } }])
      .then(() => {
        // Use a small delay to ensure the page has loaded, then trigger scroll
        setTimeout(() => {
          // Dispatch a custom event that the patient file component can listen to
          window.dispatchEvent(new CustomEvent('scrollToAppointments'));
        }, 500);
      });
  }

  private filterFutureAppointments(appointments: any[]): any[] {
    const today = moment().startOf('day');
    return appointments.filter(apt => moment(apt.date).isAfter(today) && apt.active);
  }

  private promptForFutureAppointments(futureAppointments: any[], outcome$: Subject<FutureAppointmentOutcome>) {
    const appointmentCount = futureAppointments.length;
    const message = `This patient has ${appointmentCount} upcoming appointment${appointmentCount > 1 ? 's' : ''}. What would you like to do?`;

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Future Appointments Found',
        message: message,
        confirmText: 'Review Appointments',
        cancelText: 'Cancel All',
        additionalText: 'Ignore',
        showAdditionalButton: true,
        cancelButtonTheme: 'warn',
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        // Review appointments - the caller decides where that is
        this.emit(outcome$, { action: 'review', appointments: futureAppointments });
      } else if (result === false) {
        // Cancel all appointments
        this.confirmCancelAllFutureAppointments(futureAppointments, outcome$);
      } else if (result === 'additional') {
        // Ignore
        this.emit(outcome$, { action: 'ignored', appointments: futureAppointments });
      } else {
        // Dialog closed without selection
        this.emit(outcome$, { action: 'dismissed', appointments: futureAppointments });
      }
    });
  }

  private confirmCancelAllFutureAppointments(futureAppointments: any[], outcome$: Subject<FutureAppointmentOutcome>) {
    // Show confirmation for cancelling all appointments
    const confirmDialog = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Cancel All Future Appointments',
        message: `Are you sure you want to cancel all ${futureAppointments.length} future appointments for this patient?`,
        confirmText: 'Yes, Cancel All',
        cancelText: 'No, Keep Appointments',
        cancelButtonTheme: 'warn',
        isWarning: false
      }
    });

    confirmDialog.afterClosed().subscribe(confirmed => {
      if (!confirmed) {
        this.emit(outcome$, { action: 'kept', appointments: futureAppointments });
        return;
      }
      this.performBulkAppointmentCancellation(futureAppointments, outcome$);
    });
  }

  private performBulkAppointmentCancellation(futureAppointments: any[], outcome$: Subject<FutureAppointmentOutcome>) {
    const appointmentIds = futureAppointments.map(apt => apt.id);

    if (appointmentIds.length === 0) {
      outcome$.complete();
      return;
    }

    // Cancel appointments one by one. The patient is deceased, so never send the
    // cancellation email — without a body the server defaults to notifying.
    const cancellationPromises = appointmentIds.map(appointmentId =>
      this.appointmentService.deleteAppointment(appointmentId, { notifyPatient: false }).toPromise()
    );

    Promise.all(cancellationPromises)
      .then(() => {
        this.showToastMessage(`Successfully cancelled ${appointmentIds.length} future appointment${appointmentIds.length > 1 ? 's' : ''}.`);
        this.emit(outcome$, {
          action: 'cancelled',
          appointments: futureAppointments,
          cancelledCount: appointmentIds.length
        });
      })
      .catch((error) => {
        console.error('Error cancelling appointments:', error);
        this.showToastMessage('There was an error cancelling some appointments. Please check the appointments section.');
        this.emit(outcome$, {
          action: 'cancelled',
          appointments: futureAppointments,
          cancelledCount: 0,
          error
        });
      });
  }

  private emit(outcome$: Subject<FutureAppointmentOutcome>, outcome: FutureAppointmentOutcome) {
    outcome$.next(outcome);
    outcome$.complete();
  }

  private showToastMessage(message: string) {
    this.feedbackService.showToast(message, 'OK', 3000);
  }
}
